import "./AuthenticationProvider.scss";

import { InteractionType, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { createLogger } from "logging";
import { environment } from "models/Environment";
import { PropsWithChildren } from "react";

import { AuthenticatedProvider } from "./AuthenticatedProvider";
import { defaultScopes } from "./AuthenticationContext";
import { AuthError } from "./AuthError";
import { AuthLoading } from "./AuthLoading";

const logger = createLogger("AuthenticationProvider");

const isLocal = environment.name === "local";

const pca = new PublicClientApplication({
  auth: {
    clientId: environment.clientId,
    authority: `https://login.microsoftonline.com/${environment.tenantId}`,
    redirectUri: `${window.location.origin}/`,
    postLogoutRedirectUri: `${window.location.origin}/`,
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    allowPlatformBroker: false,
    loggerOptions: {
      logLevel: isLocal ? LogLevel.Verbose : LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii && !isLocal) return;
        switch (level) {
          case LogLevel.Error:
            logger.error(message);
            break;
          case LogLevel.Warning:
            logger.warn(message);
            break;
          case LogLevel.Info:
            logger.info(message);
            break;
          case LogLevel.Verbose:
            logger.debug(message);
            break;
        }
      },
    },
  },
});

await pca.initialize();
logger.debug("pca initialized");

export const AuthenticationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={{ scopes: defaultScopes }}
        errorComponent={() => <AuthError />}
        loadingComponent={() => <AuthLoading />}
      >
        <AuthenticatedProvider>{children}</AuthenticatedProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
